import {createSlice} from './createSlice';
import {onConnectionDone, onDeviceDataChange} from '../../services/device/connectionService';
import iotSettingsService from '../../services/iot/iotSettingsService';
import errorService from '../../services/errors/errorService';
import {MW_ERRORS} from '../../consts/app/mwErrors';
import findMyDeviceStatuses from '../../consts/device/findMyDeviceStatuses';
import {onFwuFinish} from '../../services/iot/iotFwuService';

const getInitialState = () => {
    return {
        isConnectInProgress: false,
        isHolderConnectInProgress: false,
        isConnectedToIot: false,
        isHolderConnectedToIot: false,
        isFwuInProgress: false,
        isUpdateSettingsInProgress: false,
        isHolderUpdateSettingsInProgress: false,
        isResetDeviceInProgress: false,
        isHolderResetDeviceInProgress: false,
        findMyDeviceStatus: findMyDeviceStatuses.STOPPED,
        findMyDeviceDuration: 30,
        isAutoCleanInProgress: false,
        isConnectedToCm: false,
        isHolderConnectedToCm: false,
        isUamWebSocketAlive: false,
        isHolderUamWebSocketAlive: false,
        chargerData: undefined,
        holderData: undefined,
    };
};

const slice = createSlice(getInitialState());

export const getState = slice.getState;
export const setState = (data) => {
    slice.setState(data);
};

export const resetIotState = () => {
    setState(getInitialState());
};

export const setConnectInProgress = (isHolder, value, customError) => {
    if (isHolder) {
        setState({isHolderConnectInProgress: value});
    } else {
        setState({isConnectInProgress: value});
    }

    if (value) return;

    const {isConnectedToIot, isHolderConnectedToIot} = getState();
    const isIotConnected = isHolder ? isHolderConnectedToIot : isConnectedToIot;

    if (isIotConnected) {
        onConnectionDone({isHolder});
    } else {
        const error =
            customError ||
            errorService.createError(MW_ERRORS.ERROR_IOT_CONNECTION_GENERAL, 'Error while connecting to IoT');

        onConnectionDone({isHolder, error});
    }
};

export const setConnectedToIot = (isHolder, value) => {
    if (isHolder) {
        setState({isHolderConnectedToIot: value});
    } else {
        setState({isConnectedToIot: value});
    }
};

export const setUamWebSocketAlive = (isHolder, value) => {
    if (isHolder) {
        setState({isHolderUamWebSocketAlive: value});
    } else {
        setState({isUamWebSocketAlive: value});
    }
};

const isThrowError = (error) => {
    return (
        error?.code === MW_ERRORS.ERROR_IOT_FLOW_ALREADY_IN_PROGRESS.code ||
        error?.code === MW_ERRORS.ERROR_WRONG_DATA.code ||
        error?.code === MW_ERRORS.ERROR_IOT_NO_ASSET_ID.code
    );
};

export const setUpdateSettingsInProgress = (isHolder, value, error) => {
    if (isThrowError(error)) {
        iotSettingsService.onUpdateSettingsFinish(isHolder, error);
        return;
    }

    if (isHolder) {
        setState({isHolderUpdateSettingsInProgress: value});
    } else {
        setState({isUpdateSettingsInProgress: value});
    }

    if (value) return;

    iotSettingsService.onUpdateSettingsFinish(isHolder, error);
};

export const setFwuInProgress = (isHolder, value, error) => {
    if (isThrowError(error)) {
        onFwuFinish(isHolder, error);
        return;
    }

    setState({isFwuInProgress: value});

    if (value) return;

    onFwuFinish(isHolder, error);
};

export const setResetDeviceInProgress = (isHolder, value, error) => {
    if (isThrowError(error)) {
        iotSettingsService.onResetDeviceFinish(isHolder, error);
        return;
    }

    if (isHolder) {
        setState({isHolderResetDeviceInProgress: value});
    } else {
        setState({isResetDeviceInProgress: value});
    }

    if (value) return;

    iotSettingsService.onResetDeviceFinish(isHolder, error);
};

export const setFindMyDeviceStatus = (status, error, startTs) => {
    if (isThrowError(error)) {
        iotSettingsService.onFindMyDeviceFinish(error);
        return;
    }

    //STOPPED and STARTED not possible values to set. Logic of it set is implemented below
    if (status === findMyDeviceStatuses.STARTED || status === findMyDeviceStatuses.STOPPED) return;

    if (status === findMyDeviceStatuses.START || status === findMyDeviceStatuses.STOP) {
        setState({findMyDeviceStatus: status});
        return;
    }

    const duration = getState().findMyDeviceDuration * 1000;
    const curStatus = getState().findMyDeviceStatus;
    const isError = status === findMyDeviceStatuses.ERROR;
    let newStatus;

    //if error or current status stop (for success flow) set STOPPED
    if (isError || curStatus === findMyDeviceStatuses.STOP) {
        newStatus = findMyDeviceStatuses.STOPPED;
    } else {
        //find device flow is ended if no start find time or start find time + duration less then current time
        const isEnded = startTs ? startTs - new Date() + duration <= 0 : true;

        if (curStatus === findMyDeviceStatuses.START) {
            //if find my device already ended by time then mark as stopped
            newStatus = isEnded ? findMyDeviceStatuses.STOPPED : findMyDeviceStatuses.STARTED;
        }
    }

    setState({findMyDeviceStatus: newStatus});

    //return end find device time so client can stop animation at that time
    const endTs = newStatus === findMyDeviceStatuses.STARTED ? startTs + duration : 0;

    iotSettingsService.onFindMyDeviceFinish(error, endTs);
};

export const setFindMyDeviceDuration = (duration) => {
    setState({findMyDeviceDuration: duration});
};

export const setAutoCleanInProgress = (value, error) => {
    if (isThrowError(error)) {
        iotSettingsService.onAutoCleanFinish(error);
        return;
    }

    setState({isAutoCleanInProgress: value});

    if (value) return;

    iotSettingsService.onAutoCleanFinish(error);
};

export const setConnectedToCm = (isHolder, value) => {
    if (isHolder) {
        setState({isHolderConnectedToCm: value});
    } else {
        setState({isConnectedToCm: value});
    }
};

export const updateDeviceData = (isHolder, deviceData) => {
    onDeviceDataChange({isHolder, deviceData});

    const {chargerData = {}, holderData = {}} = getState();

    if (isHolder) {
        setState({holderData: {...holderData, ...deviceData}});
    } else {
        setState({chargerData: {...chargerData, ...deviceData}});
    }
};
